import React, { memo, useRef, useState, MouseEvent } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import { CompanyGroup, TooltipData } from '../types';

import classes from './referralChart.module.css';

const BAR_COLORS = ['#561B8C', '#5F0F99', '#6B21A8', '#7C3AED', '#8B5CF6'];

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

interface ReferralChartProps {
  data: CompanyGroup[];
  isLoading: boolean;
  error: string | null;
  onBarClick: (company: string) => void;
  selectedCompany: string | null;
}

function ReferralChart({ data, isLoading, error, onBarClick, selectedCompany }: ReferralChartProps) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [hoveredCompany, setHoveredCompany] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const maxValue = data.length > 0 ? Math.max(...data.map((item) => item.value)) : 0;

  const handleBarMouseEnter = (
    company: string,
    value: number,
    count: number,
    event: MouseEvent<HTMLDivElement>
  ) => {
    if (!chartRef.current) return;

    const containerRect = chartRef.current.getBoundingClientRect();
    const barRect = event.currentTarget.getBoundingClientRect();

    setHoveredCompany(company);
    setTooltip({
      company,
      value,
      count,
      left: barRect.left + barRect.width / 2 - containerRect.left,
      top: barRect.top - containerRect.top - 12,
    });
  };

  const handleBarMouseLeave = () => {
    setHoveredCompany(null);
    setTooltip(null);
  };

  const handleChartBackgroundClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onBarClick('');
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`alert alert-danger ${classes.alert}`} role="alert">
        {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`alert alert-info ${classes.alert}`} role="alert">
        No opportunities match the selected filters.
      </div>
    );
  }

  return (
    <div
      ref={chartRef}
      className={classes.chartContainer}
      onClick={handleChartBackgroundClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onBarClick('');
      }}
    >
      {data.map((item, index) => {
        const barHeight = maxValue ? Math.max((item.value / maxValue) * 220, 6) : 6;
        const color = BAR_COLORS[index % BAR_COLORS.length];
        const isActive = selectedCompany === item.company;
        const isHovered = hoveredCompany === item.company;
        const glow =
          isActive || isHovered
            ? '0 12px 28px rgba(53, 18, 99, 0.35)'
            : '0 3px 8px rgba(15, 23, 42, 0.18)';

        return (
          <div key={item.company} className={classes.barWrapper}>
            <div className={classes.barContainer}>
              <div
                className={classes.bar}
                style={{
                  height: `${barHeight}px`,
                  background: `linear-gradient(180deg, ${color}, ${color}dd)`,
                  boxShadow: glow,
                  transform: isHovered ? 'translateY(-4px)' : 'none',
                }}
                onMouseEnter={(event) =>
                  handleBarMouseEnter(item.company, item.value, item.count, event)
                }
                onMouseLeave={handleBarMouseLeave}
                onClick={() => onBarClick(item.company)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onBarClick(item.company);
                  }
                }}
                aria-label={`${item.company}: ${formatCurrency(item.value)}, ${item.count} opportunities`}
              >
                <span className={classes.barValue}>{formatCurrency(item.value)}</span>
              </div>
            </div>
            <span className={classes.barLabel}>{item.company}</span>
          </div>
        );
      })}

      {tooltip && (
        <div
          className={classes.tooltip}
          style={{
            left: `${tooltip.left}px`,
            top: `${tooltip.top}px`,
          }}
        >
          <strong>{tooltip.company}</strong>
          <div>{formatCurrency(tooltip.value)}</div>
          <small>
            {tooltip.count} {tooltip.count === 1 ? 'opportunity' : 'opportunities'}
          </small>
        </div>
      )}
    </div>
  );
}

const ReferralChartMemo = memo(ReferralChart, areEqual);

export { ReferralChartMemo as ReferralChart };
